import { showAlert } from '../components/general-components/alert-service';
import { HttpRequest } from '../stores/types/types';
import { appLogger } from '../utils/datadog';
import { buildSyncUrl, getSyncHeaders } from './utils';

import {
	ScheduledStreamStatus,
} from '../stores/types/connection';
import type {
	ScheduledStreamResponse,
	ScheduledStreamValidation,
} from '../stores/types/connection';

export const scheduledStreamActiveStates = [
	ScheduledStreamStatus.Starting,
	ScheduledStreamStatus.Started,
];

const baseRoute = 'v1/scheduled-streams';

// Type predicate for ScheduledStreamResponse
export const isStreamResponse = (
	res: ScheduledStreamResponse | {},
): res is ScheduledStreamResponse => (
	(res as ScheduledStreamResponse).id !== undefined
);

export const CreateScheduledStream = async (
	token: string,
	meetingId: number,
	userId: number,
	startTime: Date,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const minutes = 3;
		const now = new Date(new Date().getTime() + (minutes * 60000));

		if (startTime < now || !startTime) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
			appLogger.error(
				'CreateScheduledStream error: Invalid start time.',
			);
			return null;
		}

		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${userId}`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					startTime,
					status: ScheduledStreamStatus.Scheduled,
				}),
			},
		);

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();
			if (result && isStreamResponse(result)) {
				return result;
			}
		}

		if (response.status === 400 || response.status === 500) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
		}

		appLogger.error(
			'CreateScheduledStream error: Unable to create scheduled stream.',
		);
	} catch (error) {
		appLogger.error(`CreateScheduledStream error: ${error}`);
	}

	return null;
};

export const GetScheduledStream = async (
	token: string,
	meetingId: number,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const response = await fetch(buildSyncUrl(`${baseRoute}/${meetingId}`), {
			method: HttpRequest.Get,
			headers: getSyncHeaders(token),
		});

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();
			if (result && isStreamResponse(result)) {
				return result;
			}
		}

		appLogger.error(
			'GetScheduledStreams error: Unable to get scheduled streams.',
		);
	} catch (error) {
		appLogger.error(`GetScheduledStreams error: ${error}`);
	}

	return null;
};

export const UpdateScheduledStream = async (
	token: string,
	meetingId: number,
	scheduledStreamId: number,
	userId: number,
	startTime: Date,
	status: ScheduledStreamStatus,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const minutes = 3;
		const now = new Date(new Date().getTime() + (minutes * 60000));

		if (startTime < now || !startTime) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
			appLogger.error(
				'CreateScheduledStream error: Invalid start time.',
			);
			return null;
		}
		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${scheduledStreamId}/${userId}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					startTime,
					status,
				}),
			},
		);

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();
			if (result && isStreamResponse(result)) {
				return result;
			}
		}
		if (response.status === 400 || response.status === 500) {
			showAlert({
				message: 'Cannot schedule stream',
				description: 'Must be 3+ minutes in the future',
				type: 'scheduled-stream-schedule-error',
				duration: 5000,
			});
		}
		appLogger.error(
			'UpdateScheduledStream error: Unable to update scheduled stream.',
		);
	} catch (error) {
		appLogger.error(`UpdateScheduledStream error: ${error}`);
	}

	return null;
};

export const UpdateScheduledStreamStatus = async (
	token: string,
	meetingId: number,
	scheduledStreamId: number,
	userId: number,
	status: ScheduledStreamStatus,
): Promise<ScheduledStreamResponse | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${scheduledStreamId}/${userId}/${status}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			const result: ScheduledStreamResponse | {} = await response.json();

			if (result && isStreamResponse(result)) {
				return result;
			}
		}

		appLogger.error(
			'UpdateScheduledStreamStatus error: Unable to update scheduled stream status.',
		);
	} catch (error) {
		appLogger.error(`UpdateScheduledStreamStatus error: ${error}`);
	}

	return null;
};

export const DeleteScheduledStream = async (
	token: string,
	meetingId: number,
	scheduledStreamId: number,
	userId: number,
): Promise<boolean> => {
	try {
		const response = await fetch(
			buildSyncUrl(`${baseRoute}/${meetingId}/${scheduledStreamId}/${userId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			const outcome: boolean = await response.json();

			if (outcome) {
				return true;
			}
		}

		appLogger.error(
			'DeleteScheduledStream error: Unable to delete scheduled stream.',
		);
	} catch (error) {
		appLogger.error(`DeleteScheduledStream error: ${error}`);
	}

	return false;
};

export const GetScheduledStreamValidation = async (
	token: string,
	meetingId: number,
): Promise<ScheduledStreamValidation[]> => {
	try {
		const response = await fetch(
			buildSyncUrl(`${baseRoute}-validation/${meetingId}`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error('GetScheduledStreamValidation error: Unable to get validation.');
	} catch (error) {
		appLogger.error(`GetScheduledStreamValidation error: ${error}`);
	}

	return [];
};
